import React, { useRef, useEffect } from 'react';
import { MessageListProps } from './types';
import { MessageContent } from './MessageContent';

/**
 * Formata timestamp para exibição
 */
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);

  if (isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Agora';
  if (minutes < 60) return `${minutes}m atrás`;
  if (hours < 24) return `${hours}h atrás`;
  if (days < 7) return `${days}d atrás`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Componente para exibir lista de mensagens
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  enableMarkdown = true,
  showTimestamps = true,
  customStyles = {},
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessagesLengthRef = useRef(0);

  // Auto-scroll: apenas quando houver uma nova mensagem
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Verifica se há uma nova mensagem
    const hasNewMessage = messages.length > previousMessagesLengthRef.current;

    // Atualiza o contador
    previousMessagesLengthRef.current = messages.length;

    // Rola para o final APENAS se houver uma nova mensagem
    if (hasNewMessage) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  return (
    <div ref={messagesContainerRef} style={customStyles.container}>
      {isLoading && messages.length === 0 && (
        <div style={customStyles.loadingIndicator}>
          Carregando mensagens...
        </div>
      )}

      {messages.map((message) => {
        const isUser = message.sender === 'customer';
        const messageStyle = isUser ? customStyles.messageUser : customStyles.messageBot;
        const isInitialMessage = message.id.startsWith('initial-message-');

        return (
          <div key={message.id} style={messageStyle}>
            <MessageContent
              content={message.message}
              enableMarkdown={enableMarkdown}
              isUser={isUser}
              customStyles={customStyles.messageContent}
            />
            {showTimestamps && !isInitialMessage && (
              <div style={customStyles.messageTimestamp}>
                {formatTimestamp(message.timestamp)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
